<script lang="ts">
	// (c) Cognisoft Aps, 2022
	// base component for entire single page app
	// - routing to pages - not reflected on url address line
	// - so no direct acces for user to pages via url

	import { Pages } from "./enums";
	import { route } from "./stores/route.svelte";

	import { isOnline } from "./stores/online-status.svelte";

	import Login from "./pages/Login.svelte";
	import AdminStart from "./pages/AdminStart.svelte";
	import PrivateEdit from "./pages/PrivateEdit.svelte";
	import CenterEdit from "./pages/CenterEdit.svelte";
	import SLTEdit from "./pages/SLTEdit.svelte";
	import PWAEdit from "./pages/PWAEdit.svelte";
	import ResultList from "./pages/ResultList.svelte";
	import ResultDetails from "./pages/ResultDetails.svelte";
	import ResultCsvData from "./pages/ResultCsvData.svelte";
	import Settings from "./pages/Settings.svelte";
	import Ready from "./pages/Ready.svelte";
	import Step1 from "./pages/Step1.svelte";
	import Step2 from "./pages/Step2.svelte";
	import Step3 from "./pages/Step3.svelte";
	import Step4 from "./pages/Step4.svelte";
	import Step5 from "./pages/Step5.svelte";
	import Step6 from "./pages/Step6.svelte";
	import Step7 from "./pages/Step7.svelte";
	import Step8 from "./pages/Step8.svelte";
	import Step9 from "./pages/Step9.svelte";
	import EndResults from "./pages/EndResults.svelte";
	import RoutingError from "./pages/RoutingError.svelte";
	import ResultsLineChart from "./pages/ResultsLineChart.svelte";

	// STATE ---------------------------------------------

	// reference to audio tag in this base component to be used throughout app
	//  in order to have sound played even before mouse click
	let audioTag: HTMLAudioElement;

	// PWA - serviceworker

	if (navigator.serviceWorker) {
		navigator.serviceWorker.register("../serviceworker.js");
	}

	if (navigator.storage && navigator.storage.persist) {
		navigator.storage.persist();
	}

	// test for on/off-line
	//sOnline.value = navigator.onLine;

	// WINDOW SIZE FOR MOBILE BROWSERS HACK --------------

	// First we get the viewport height and we multiple it by 1% to get a value for a vh unit
	let vh = window.innerHeight * 0.01;
	// Then we set the value in the --vh custom property to the root of the document
	document.documentElement.style.setProperty("--vh", `${vh}px`);
	// We listen to the resize event
	window.addEventListener("resize", () => {
		// We execute the same script as before
		let vh = window.innerHeight * 0.01;
		document.documentElement.style.setProperty("--vh", `${vh}px`);
	});

	/* use like this:
	.my-element {
  	height: calc(var(--vh, 1vh) * 100);
	*/

	// next 3 events to prevent pinch zoom on iPad:
	document.addEventListener("gesturestart", function (e) {
		e.preventDefault();
		// special hack to prevent zoom-to-tabs gesture in safari
	});

	document.addEventListener("gesturechange", function (e) {
		e.preventDefault();
		// special hack to prevent zoom-to-tabs gesture in safari
	});

	document.addEventListener("gestureend", function (e) {
		e.preventDefault();
		// special hack to prevent zoom-to-tabs gesture in safari
	});

	// prevent zoom on double tap on iPad:
	let lastTouchEnd = 0;
	const disableDoubletapZoom = (e: TouchEvent) => {
		let now = new Date().getTime();
		if (now - lastTouchEnd <= 300) {
			e.preventDefault();
		}
		lastTouchEnd = now;
	};

	// Goin on- or off-line

	const handleOffLine = () => {
		isOnline.value = false;
	};

	const handleOnLine = () => {
		isOnline.value = true;
	};

	// Warn user not to leave page
	function askBeforeUnload(event: BeforeUnloadEvent): string {
		event.preventDefault();
		//event.returnValue = "Du er ved at forlade MODAK";
		return "Du er ved at forlade MODAK";
	}
</script>

<svelte:window
	onbeforeunload={askBeforeUnload}
	ontouchend={disableDoubletapZoom}
	onoffline={handleOffLine}
	ononline={handleOnLine}
/>

<main>
	<audio bind:this={audioTag}></audio>

	<!-- internal SPA routing - NOT reflected on url address line -->
	{#if route.current === Pages.Login}
		<Login {audioTag} />
	{:else if route.current === Pages.AdminStart}
		<AdminStart />
	{:else if route.current === Pages.CenterEdit}
		<CenterEdit />
	{:else if route.current === Pages.PrivateEdit}
		<PrivateEdit />
	{:else if route.current === Pages.SLTEdit}
		<SLTEdit />
	{:else if route.current === Pages.PWAEdit}
		<PWAEdit />
	{:else if route.current === Pages.ResultList}
		<ResultList />
	{:else if route.current === Pages.ResultDetails}
		<ResultDetails />
	{:else if route.current === Pages.ResultsLineChart}
		<ResultsLineChart />
	{:else if route.current === Pages.ResultCsvData}
		<ResultCsvData />
	{:else if route.current === Pages.Ready}
		<Ready {audioTag} />
	{:else if route.current === Pages.Step1}
		<Step1 {audioTag} />
	{:else if route.current === Pages.Step2}
		<Step2 {audioTag} />
	{:else if route.current === Pages.Step3}
		<Step3 {audioTag} />
	{:else if route.current === Pages.Step4}
		<Step4 {audioTag} />
	{:else if route.current === Pages.Step5}
		<Step5 {audioTag} />
	{:else if route.current === Pages.Step6}
		<Step6 {audioTag} />
	{:else if route.current === Pages.Step7}
		<Step7 {audioTag} />
	{:else if route.current === Pages.Step8}
		<Step8 {audioTag} />
	{:else if route.current === Pages.Step9}
		<Step9 {audioTag} />
	{:else if route.current === Pages.EndResults}
		<EndResults />
	{:else if route.current === Pages.Settings}
		<Settings />
	{:else}
		<RoutingError>currentRoute: {route.current}</RoutingError>
	{/if}
</main>
